import React, { useState } from "react";
import axios from "axios";
import { useTheme } from "../config/ThemeContext";
import { Link, useNavigate } from "react-router-dom";

const Signup = () => {
  // Determine mode from environment variable
  const isCloudMode = process.env.REACT_APP_AUTH_MODE === "supabase" || 
                     window.location.hostname !== "localhost";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const colors = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    
    // Validation
    if (!email || !password || !confirmPassword) {
      setErrorMsg("All fields are required");
      return;
    }
    
    if (password !== confirmPassword) {
      setErrorMsg("Passwords do not match");
      return;
    }
    
    if (password.length < 6) {
      setErrorMsg("Password must be at least 6 characters");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMsg("Please enter a valid email address");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/signup`,
        { email, password },
        { withCredentials: true }
      );
      
      const { message, requiresEmailConfirmation, token } = response.data;
      
      if (requiresEmailConfirmation) {
        setSuccessMsg(message);
        // Don't auto-login, show success message
      } else {
        // Cookie session already set by backend
        window.location.href = "/"; // Use window.location to trigger a full refresh
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.error || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-sm">
        <img
          src="/assets/logos/volleyfly-logo-300x111.png"
          alt="VolleyFly"
          className="mx-auto pb-4"
        />
        
        {/* Show mode badge */}
        <div className={`text-center mb-2 text-sm font-semibold ${
          isCloudMode ? "text-blue-600" : "text-green-600"
        }`}>
          {isCloudMode ? "🌐 Cloud Version" : "💻 Local Version"}
        </div>
        
        <h2 className="text-xl font-bold text-center text-gray-800 mb-2">
          Create Account
        </h2>
        
        <center className="mb-4">
          <i>"Ain't no volley high enough!"</i>
        </center>
        
        {errorMsg && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg p-3 mb-4">
            {errorMsg}
          </div>
        )}
        
        {successMsg && (
          <div className="bg-green-50 border border-green-200 text-green-600 text-sm rounded-lg p-3 mb-4">
            {successMsg}
            <div className="mt-2">
              <Link 
                to="/" 
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Go to Login
              </Link>
            </div>
          </div>
        )}
        
        {!successMsg && (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                placeholder="At least 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className={`w-full ${colors.primary.bg} text-white font-semibold py-2 rounded-lg hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? "Creating Account..." : "Sign Up"}
            </button>
          </form>
        )}
        
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-center text-gray-600 text-sm">
            Already have an account?{" "}
            <Link 
              to="/" 
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Log In
            </Link>
          </p>
        </div>
        
        <div className="mt-4 text-xs text-gray-500 text-center">
          By signing up, you agree to our <a href="https://www.volleyfly.com/terms.html">Terms of Service</a> and <a href="https://www.volleyfly.com/privacy.html">Privacy Policy</a>.
        </div>
      </div>
    </div>
  );
};

export default Signup;
